import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Route,
	Router,
	RouterStateSnapshot,
	UrlSegment,
	UrlTree,
} from '@angular/router';
import { HTTPStatusCodes } from '@asamco/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { AsamcoAuthService } from './auth/asamco-auth.service';
import { redirectDestinationName } from './auth/auth.consts';
import { HttpClient } from '@angular/common/http';

/**
 * a router guard that prevents invoking routes when a user is not logged in
 * will redirect to the login page if such a route is opened anyway.
 * the default setup of the appshell makes sure that all, but a few top routes are
 * authenticated. Details can be seen in app-shell.route
 */
@Injectable({
	providedIn: 'root',
	// deps: [ActivatedRouteSnapshot, Router, AppSettings]
	// deps: [TenantService]
})
export class AsamcoAuthGuard {
	/**
	 * keep tabs when someone logs in/out  keep this in the component,
	 * and buffer it, so we don't create a new subscription on every call.
	 */
	private readonly isLoggedIn$ = this.authService.isLoggedIn$.pipe(shareReplay(1));

	/**
	 * constructor
	 */
	constructor(private authService: AsamcoAuthService, private httpClient: HttpClient, private router: Router) {}

	canMatch(route: Route, segments: UrlSegment[]): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
		if (this.hasCypressAuthBypass()) {
			return true;
		}

		return this.isLoggedIn$.pipe(
			take(1),
			/**
			 * In an attempt to not redirect back to /auth every. single. page load
			 * the startWith(false) was removed in the _isLoggedIn$ and as a last step of
			 * loadOidcConfig the auth.tryRestoreLastSession was called. This doesn't
			 * work as the canLoad is called before loading the app, and thus the APP_INITIALIZERs.
			 *
			 * See: https://github.com/angular/angular/issues/14588
			 * and https://github.com/angular/angular/issues/29828
			 *
			 * To make this work anyway, we call tryRestoreLastSession for canLoad
			 * when _isLoggedIn$ is false, this allows deep-linking and the whole shebang.
			 *
			 * Ideally we should implement a better entrypoint for our login flow (hard when
			 * wanting to support all providers properly, because they behave differently).
			 * Essentially eliminating the startWith(false) of the _isLoggedIn$ observable in
			 * the AsamcoAuthService.
			 */
			switchMap(isLoggedIn => {
				if (isLoggedIn) {
					return of(isLoggedIn);
				} else {
					return this.authService.tryRestoreLastSession().pipe(
						map(loginResult => loginResult.loginStatusCode === HTTPStatusCodes.OK),
						catchError(() => of(false))
					);
				}
			}),
			map(isLoggedIn => isLoggedIn || this.formatRedirect())
		);
	}

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
		if (this.hasCypressAuthBypass()) {
			return true;
		}

		return this.isLoggedIn$.pipe(
			take(1),
			map(isLoggedIn => isLoggedIn || this.formatRedirect())
		);
	}

	/**
	 * canActivate callback that will be used by the router to check if an user is authenticated
	 */
	canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		if (this.hasCypressAuthBypass()) {
			return true;
		}

		return this.isLoggedIn$.pipe(
			take(1),
			map(isLoggedIn => {
				if (!isLoggedIn && route.url.length === 0) {
					return false;
				}
				if (!isLoggedIn && route.url[0].path !== 'auth') {
					return this.formatRedirect();
				}
				return true;
			})
		);
	}

	private formatRedirect() {
		return this.router.createUrlTree(['auth', 'login'], {
			queryParams: {
				[redirectDestinationName]: this.router.getCurrentNavigation().extractedUrl.toString(),
			},
		});
	}

	private hasCypressAuthBypass(): boolean {
		return typeof window !== 'undefined'
			&& !!(window as any).Cypress
			&& window.localStorage?.getItem('application-link:e2e-auth-bypass') === 'true';
	}
}
